import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Form,
  Row,
  Col,
  Container,
  Spinner,
} from 'react-bootstrap';
import { FaFileExcel, FaSearch, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import { authAxios } from '../utils/reportUtils';

const DriversReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [branchId, setBranchId] = useState('');
  const [showrooms, setShowrooms] = useState([]);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    // Fetch showrooms for the filter
    const fetchShowrooms = async () => {
      try {
        const response = await authAxios.get('/showrooms');
        const rawList = response.data?.data?.data || response.data?.data || response.data || [];
        const list = Array.isArray(rawList) ? rawList : [];
        setShowrooms(list);
      } catch (error) {
        console.error('Error fetching showrooms:', error);
      }
    };
    fetchShowrooms();
  }, []);

  const calculateTimeTaken = (start, end) => {
    if (!start || !end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    const diffMins = Math.round(diffMs / 60000);
    return diffMins; // Returns difference in minutes
  };

  const formatDuration = (minutes) => {
    if (minutes === null || isNaN(minutes)) return 'N/A';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const fetchReport = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setReportData([]);

    try {
      const dTo = new Date(dateTo);
      dTo.setHours(23, 59, 59, 999);
      const dFrom = new Date(dateFrom);
      dFrom.setHours(0, 0, 0, 0);

      let allOrders = [];
      let page = 1;
      let lastPage = 1;

      // Fetch Page Loop
      while (page <= lastPage) {
        if (abortControllerRef.current?.signal.aborted) break;

        const params = {
          page,
          per_page: 50,
        };
        if (branchId) {
          params.showroom_id = branchId;
        }

        // Use public /orders endpoint
        const res = await authAxios.get('/orders', {
          params,
          signal: abortControllerRef.current.signal
        });

        const responseData = res.data?.data;
        const ordersData = Array.isArray(responseData?.data)
          ? responseData.data
          : Array.isArray(responseData)
            ? responseData
            : [];

        // Pagination info
        const metaLastPage = responseData?.last_page || responseData?.meta?.last_page || res.data?.last_page || 1;
        lastPage = metaLastPage;

        let stopFetching = false;
        const filteredPage = [];

        for (const order of ordersData) {
          const oDate = new Date(order.created_at);

          if (oDate > dTo) continue;
          if (oDate < dFrom) {
            stopFetching = true;
            break;
          }
          filteredPage.push(order);
        }

        allOrders = [...allOrders, ...filteredPage];

        if (stopFetching) break;
        if (ordersData.length === 0) break;

        page++;
      }

      // Manual Calculation
      const driverStats = {};

      allOrders.forEach(order => {
        // Filter by branch if selected - backend should handle this via showroom_id but good to safety check
        if (branchId && order.showroom_id !== parseInt(branchId)) return;

        if (order.status === 'delivered' && order.driver_id) {
          const driverId = order.driver_id;
          const driverName = order.driver?.name || 'Unknown Driver';
          const branchName = order.showroom?.name || 'N/A';

          if (!driverStats[driverId]) {
            driverStats[driverId] = {
              driver_name: driverName,
              branch_name: branchName,
              total_deliveries: 0,
              total_time_minutes: 0,
              count_with_time: 0
            };
          }

          driverStats[driverId].total_deliveries += 1;

          const logs = order.status_logs || order.statusLogs || [];
          const pickedUp = logs.find(l => l.to_status === 'picked_up')?.created_at;
          const delivered = logs.find(l => l.to_status === 'delivered')?.created_at || order.delivered_at || order.updated_at;

          if (pickedUp && delivered) {
            const timeTaken = calculateTimeTaken(pickedUp, delivered);
            if (timeTaken !== null) {
              driverStats[driverId].total_time_minutes += timeTaken;
              driverStats[driverId].count_with_time += 1;
            }
          }
        }
      });

      const processedData = Object.values(driverStats).map(stat => ({
        ...stat,
        avg_delivery_time: stat.count_with_time > 0
          ? formatDuration(Math.round(stat.total_time_minutes / stat.count_with_time))
          : 'N/A'
      }));

      setReportData(processedData);

    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching/calculating drivers report:', error);
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setLoading(false);
      }
    }
  }, [dateFrom, dateTo, branchId]);

  useEffect(() => {
    fetchReport();
    return () => abortControllerRef.current?.abort();
  }, [fetchReport]);

  const handleSearch = () => {
    fetchReport();
  };

  const handleExportToExcel = () => {
    if (!reportData.length) {
      alert('No data to export');
      return;
    }

    const exportData = reportData.map((item) => ({
      'Driver Name': item.driver_name,
      'Branch Name': item.branch_name,
      'Total Deliveries': item.total_deliveries,
      'Avg Delivery Time': item.avg_delivery_time || 'N/A',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Drivers Report');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
        type: 'application/octet-stream',
      }),
      `drivers_report_${dateFrom}_to_${dateTo}.xlsx`
    );
  };

  const columns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
      width: '50px',
    },
    {
      name: 'Driver Name',
      selector: (row) => row.driver_name,
      sortable: true,
    },
    {
      name: 'Branch Name',
      selector: (row) => row.branch_name,
      sortable: true,
    },
    {
      name: 'Total Deliveries',
      selector: (row) => row.total_deliveries,
      sortable: true,
      center: true,
    },
    {
      name: 'Avg Delivery Time',
      selector: (row) => row.avg_delivery_time,
      sortable: false,
      center: true,
    },
  ];

  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>Loading report...</span>
    </div>
  );

  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Data Found</h5>
      <p className='mb-0'>
        There is currently no driver data for the selected dates.
      </p>
    </div>
  );

  return (
    <Container fluid>
      <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
        Drivers Report
      </h3>

      {/* Filters */}
      <Row className='justify-content-between align-items-center mt-3 mb-3'>
        <Col md={8}>
          <div className='d-flex align-items-center gap-3'>
            <Form.Control
              type='date'
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ width: '160px' }}
              title="Start Date"
            />
            <span className="text-muted">to</span>
            <Form.Control
              type='date'
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ width: '160px' }}
              title="End Date"
            />

            <Form.Select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              style={{ width: '200px' }}
              disabled={loading}
            >
              <option value="">All Branches</option>
              {showrooms.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Form.Select>
            <Button
              variant='primary'
              size='sm'
              onClick={handleSearch}
              disabled={loading}
            >
              <FaSearch className='me-1' />
              Refresh
            </Button>
          </div>
        </Col>

        <Col md={4} className='text-end'>
          <Button size='sm' variant='success' onClick={handleExportToExcel}>
            <FaFileExcel /> Export
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ maxHeight: '59vh', position: 'relative' }}>
        {/* Count Display */}
        {reportData.length > 0 && (
          <div
            className='text-end p-2'
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <small className='text-muted'>
              Showing <strong>{1}</strong> to <strong>{reportData.length}</strong>{' '}
              out of <strong>{reportData.length}</strong> drivers
            </small>
          </div>
        )}
        <DataTable
          columns={columns}
          data={reportData}
          pagination={false}
          highlightOnHover
          striped
          dense
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight='59vh'
          progressPending={loading}
          progressComponent={CustomLoadingIndicator}
          noDataComponent={CustomNoDataComponent}
          customStyles={{
            table: {
              style: {
                tableLayout: 'auto',
                width: '100%',
                maxWidth: '100%',
              },
            },
            tableWrapper: {
              style: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
              },
            },
            headRow: {
              style: {
                backgroundColor: '#f8f9fa',
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
                borderBottomColor: '#dee2e6',
              },
            },
            headCells: {
              style: {
                fontSize: '14px',
                fontWeight: '600',
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            },
            cells: {
              style: {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
            },
          }}
        />
        {!loading && reportData.length > 0 && (
          <div className='text-center p-3 text-muted'>
            <small>All orders loaded ({reportData.length} total)</small>
          </div>
        )}
      </div>
    </Container>
  );
};

export default DriversReport;
